import React, { useState, useEffect } from "react";
import {
  Box,
  TextField,
  Typography,
  Button,
  IconButton,
  Stack,
  Chip,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Snackbar,
  FormHelperText,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import CloseIcon from "@mui/icons-material/Close";

const JobFormWithTable = () => {
  const [openModal, setOpenModal] = useState(false);
  const [jobs, setJobs] = useState([]);
  const [editingJob, setEditingJob] = useState(null);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [jobToDelete, setJobToDelete] = useState(null);
  const [loading, setLoading] = useState(false);
  const [alert, setAlert] = useState({ open: false, message: '', severity: 'success' });
  const [errors, setErrors] = useState({});

  const [job, setJob] = useState({
    title: "",
    description: "",
    experience: "",
    qualification: "",
    skills: "",
  });

  // Fetch jobs from API
  const fetchJobs = async () => {
    try {
      setLoading(true);
      const response = await fetch('/api/jobdata');
      if (response.ok) {
        const data = await response.json();
        setJobs(data);
      } else {
        showAlert('Failed to fetch jobs', 'error');
      }
    } catch (error) {
      console.error('Error fetching jobs:', error);
      showAlert('Error fetching jobs', 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchJobs();
  }, []);

  const showAlert = (message, severity = 'success') => {
    setAlert({ open: true, message, severity });
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setJob({ ...job, [name]: value });
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors({ ...errors, [name]: '' });
    }
  };

  const validateForm = () => {
    const newErrors = {};

    // Title validation
    if (!job.title.trim()) {
      newErrors.title = 'Job title is required';
    } else if (job.title.trim().length < 3) {
      newErrors.title = 'Job title must be at least 3 characters';
    } else if (job.title.trim().length > 100) {
      newErrors.title = 'Job title must not exceed 100 characters';
    }

    // Description validation
    if (!job.description.trim()) {
      newErrors.description = 'Job description is required';
    } else if (job.description.trim().length < 10) {
      newErrors.description = 'Job description must be at least 10 characters';
    } else if (job.description.trim().length > 1000) {
      newErrors.description = 'Job description must not exceed 1000 characters';
    }

    // Experience validation
    if (!job.experience.trim()) {
      newErrors.experience = 'Experience requirement is required';
    } else if (job.experience.trim().length < 2) {
      newErrors.experience = 'Experience must be at least 2 characters';
    }

    // Qualification validation
    if (!job.qualification.trim()) {
      newErrors.qualification = 'Qualification is required';
    } else if (job.qualification.trim().length < 3) {
      newErrors.qualification = 'Qualification must be at least 3 characters';
    }

    // Skills validation
    if (!job.skills.trim()) {
      newErrors.skills = 'Skills are required';
    } else {
      const skillsArray = job.skills.split(',').map(skill => skill.trim()).filter(skill => skill.length > 0);
      if (skillsArray.length === 0) {
        newErrors.skills = 'At least one skill is required';
      } else if (skillsArray.some(skill => skill.length < 2)) {
        newErrors.skills = 'Each skill must be at least 2 characters';
      } else if (skillsArray.length > 20) {
        newErrors.skills = 'Maximum 20 skills allowed';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      showAlert('Please fix the validation errors', 'error');
      return;
    }

    setLoading(true);

    try {
      // Convert skills string to array (split by comma, trim whitespace)
      const skillsArray = job.skills
        .split(',')
        .map(skill => skill.trim())
        .filter(skill => skill.length > 0);

      const jobData = {
        title: job.title.trim(),
        description: job.description.trim(),
        experience: job.experience.trim(),
        qualification: job.qualification.trim(),
        skills: skillsArray,
      };

      const url = editingJob ? `/api/jobdata/${editingJob._id}` : '/api/jobdata';
      const method = editingJob ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method: method,
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(jobData),
      });

      if (response.ok) {
        showAlert(editingJob ? 'Job updated successfully!' : 'Job created successfully!');
        resetForm();
        fetchJobs(); // Refresh the jobs list
      } else {
        const errorData = await response.json();
        showAlert(errorData.error || 'Failed to save job', 'error');
      }
    } catch (error) {
      console.error('Error saving job:', error);
      showAlert('Error saving job', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (jobToEdit) => {
    setJob({
      title: jobToEdit.title,
      description: jobToEdit.description,
      experience: jobToEdit.experience,
      qualification: jobToEdit.qualification,
      skills: jobToEdit.skills.join(', '), // Convert array back to comma-separated string
    });
    setEditingJob(jobToEdit);
    setErrors({});
    setOpenModal(true);
  };

  const handleDelete = async () => {
    if (!jobToDelete) return;

    setLoading(true);
    try {
      const response = await fetch(`/api/jobdata/${jobToDelete._id}`, {
        method: 'DELETE',
      });

      if (response.ok) {
        showAlert('Job deleted successfully!');
        fetchJobs(); // Refresh the jobs list
      } else {
        const errorData = await response.json();
        showAlert(errorData.error || 'Failed to delete job', 'error');
      }
    } catch (error) {
      console.error('Error deleting job:', error);
      showAlert('Error deleting job', 'error');
    } finally {
      setLoading(false);
      setOpenDeleteDialog(false);
      setJobToDelete(null);
    }
  };

  const resetForm = () => {
    setJob({
      title: "",
      description: "",
      experience: "",
      qualification: "",
      skills: "",
    });
    setEditingJob(null);
    setErrors({});
    setOpenModal(false);
  };

  const openDeleteConfirmation = (job) => {
    setJobToDelete(job);
    setOpenDeleteDialog(true);
  };

  const handleOpenModal = () => {
    setErrors({});
    setOpenModal(true);
  };

  return (
    <Box sx={{ maxWidth: "1200px", mx: "auto", p: 4 }}>
      {/* Add Job Button */}
      <Box sx={{ display: "flex", justifyContent: "flex-start", mb: 3 }}>
        <Button 
          variant="contained" 
          onClick={handleOpenModal} 
          disabled={loading}
          size="large"
          sx={{ px: 4 }}
        >
          Add New Job
        </Button>
      </Box>

      {/* Job Form Modal */}
      <Dialog 
        open={openModal} 
        onClose={resetForm}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: { borderRadius: 3 }
        }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h5" component="div">
              {editingJob ? 'Edit Job' : 'Add New Job'}
            </Typography>
            <IconButton onClick={resetForm} disabled={loading}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>

        <DialogContent>
          <Box component="form" onSubmit={handleSubmit} sx={{ mt: 1 }}>
            <Stack spacing={3}>
              <TextField
                label="Job Title"
                name="title"
                fullWidth
                required
                value={job.title}
                onChange={handleChange}
                error={!!errors.title}
                helperText={errors.title}
                placeholder="e.g. Senior Software Engineer"
              />
              
              <TextField
                label="Job Description"
                name="description"
                multiline
                rows={4}
                fullWidth
                required
                value={job.description}
                onChange={handleChange}
                error={!!errors.description}
                helperText={errors.description}
                placeholder="Describe the role, responsibilities, and requirements..."
              />
              
              <TextField
                label="Experience Required"
                name="experience"
                fullWidth
                required
                value={job.experience}
                onChange={handleChange}
                error={!!errors.experience}
                helperText={errors.experience}
                placeholder="e.g. 3-5 years, Fresh Graduate, 2+ years"
              />
              
              <TextField
                label="Qualification"
                name="qualification"
                fullWidth
                required
                value={job.qualification}
                onChange={handleChange}
                error={!!errors.qualification}
                helperText={errors.qualification}
                placeholder="e.g. Bachelor's in Computer Science, MBA, etc."
              />
              
              <Box>
                <TextField
                  label="Skills"
                  name="skills"
                  multiline
                  rows={3}
                  fullWidth
                  required
                  value={job.skills}
                  onChange={handleChange}
                  error={!!errors.skills}
                  placeholder="e.g. JavaScript, React, Node.js, MongoDB, Python"
                />
                {errors.skills ? (
                  <FormHelperText error>{errors.skills}</FormHelperText>
                ) : (
                  <FormHelperText>Enter skills separated by commas (max 20 skills)</FormHelperText>
                )}
              </Box>
            </Stack>
          </Box>
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button 
            onClick={resetForm} 
            variant="outlined"
            disabled={loading}
            size="large"
          >
            Cancel
          </Button>
          <Button 
            onClick={handleSubmit}
            variant="contained" 
            disabled={loading}
            size="large"
            sx={{ px: 4 }}
          >
            {loading ? 'Saving...' : (editingJob ? 'Update Job' : 'Create Job')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Table of Jobs */}
      {jobs.length > 0 && (
        <Box>
          <Typography variant="h5" mb={2}>
            Job Listings ({jobs.length})
          </Typography>

          <TableContainer component={Paper} sx={{ borderRadius: 2 }}>
            <Table>
              <TableHead sx={{ backgroundColor: "#1976d2" }}>
                <TableRow>
                  <TableCell sx={{ color: "white", fontWeight: 'bold' }}>Job Title</TableCell>
                  <TableCell sx={{ color: "white", fontWeight: 'bold' }}>Description</TableCell>
                  <TableCell sx={{ color: "white", fontWeight: 'bold' }}>Experience</TableCell>
                  <TableCell sx={{ color: "white", fontWeight: 'bold' }}>Qualification</TableCell>
                  <TableCell sx={{ color: "white", fontWeight: 'bold' }}>Skills</TableCell>
                  <TableCell sx={{ color: "white", fontWeight: 'bold', textAlign: 'center' }}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {jobs.map((jobItem, index) => (
                  <TableRow key={jobItem._id || index} hover>
                    <TableCell>
                      <Typography variant="subtitle2" fontWeight={500}>
                        {jobItem.title}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography 
                        variant="body2" 
                        sx={{ 
                          maxWidth: 200, 
                          overflow: 'hidden', 
                          textOverflow: 'ellipsis',
                          display: '-webkit-box',
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: 'vertical'
                        }}
                      >
                        {jobItem.description}
                      </Typography>
                    </TableCell>
                    <TableCell>{jobItem.experience}</TableCell>
                    <TableCell>{jobItem.qualification}</TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={0.5} flexWrap="wrap" sx={{ maxWidth: 200 }}>
                        {jobItem.skills && jobItem.skills.slice(0, 3).map((skill, idx) => (
                          <Chip 
                            key={idx} 
                            label={skill} 
                            size="small" 
                            sx={{ mb: 0.5, fontSize: '0.75rem' }} 
                          />
                        ))}
                        {jobItem.skills && jobItem.skills.length > 3 && (
                          <Chip 
                            label={`+${jobItem.skills.length - 3} more`} 
                            size="small" 
                            variant="outlined"
                            sx={{ mb: 0.5, fontSize: '0.75rem' }} 
                          />
                        )}
                      </Stack>
                    </TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={1} justifyContent="center">
                        <IconButton
                          color="primary"
                          onClick={() => handleEdit(jobItem)}
                          disabled={loading}
                          size="small"
                          title="Edit Job"
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                        <IconButton
                          color="error"
                          onClick={() => openDeleteConfirmation(jobItem)}
                          disabled={loading}
                          size="small"
                          title="Delete Job"
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      )}

      {jobs.length === 0 && !loading && (
        <Paper sx={{ p: 4, textAlign: 'center', borderRadius: 2 }}>
          <Typography variant="h6" color="text.secondary" gutterBottom>
            No jobs found
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Create your first job posting to get started!
          </Typography>
        </Paper>
      )}

      {/* Delete Confirmation Dialog */}
      <Dialog open={openDeleteDialog} onClose={() => setOpenDeleteDialog(false)}>
        <DialogTitle>
          <Typography variant="h6" color="error">
            Confirm Delete
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete the job <strong>"{jobToDelete?.title}"</strong>? 
            <br />
            This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => setOpenDeleteDialog(false)} 
            disabled={loading}
            variant="outlined"
          >
            Cancel
          </Button>
          <Button 
            onClick={handleDelete} 
            color="error" 
            disabled={loading}
            variant="contained"
          >
            {loading ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Alert Snackbar */}
      <Snackbar
        open={alert.open}
        autoHideDuration={4000}
        onClose={() => setAlert({ ...alert, open: false })}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setAlert({ ...alert, open: false })}
          severity={alert.severity}
          sx={{ width: '100%' }}
        >
          {alert.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default JobFormWithTable;
